import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Calendar
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import userService from '../../services/userService'

const UserMasterEdit = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [roles, setRoles] = useState([])
  const [formData, setFormData] = useState({
    userCode: '',
    name: '',
    email: '',
    userEmail: '',
    role: '',
    department: '',
    designation: '',
    phone: '',
    status: 'Active',
    password: '',
    confirmPassword: '',
    createdDate: new Date().toISOString().split('T')[0]
  })

  // Load roles on component mount
  useEffect(() => {
    loadRoles()
  }, [])

  // Load user data when component mounts
  useEffect(() => {
    const loadUserData = async () => {
      try {
        setInitialLoading(true)
        const response = await userService.getUserById(id)
        if (response.success) {
          const userData = response.data
          const display = userService.transformUserForDisplay(userData)
          const firstRole = Array.isArray(userData.role) && userData.role[0]
          const roleId = display?.roleId || (firstRole && (typeof firstRole === 'object' ? (firstRole._id || firstRole.id) : firstRole)) || ''
          const departmentValue = display?.departmentName || userData.departmentName || (userData.department?.name) || ''
          setFormData({
            userCode: userData.userCode || '',
            name: userData.name || '',
            email: userData.email || userData.userEmail || '',
            userEmail: userData.userEmail || userData.email || '',
            role: roleId,
            department: departmentValue,
            designation: userData.designation || '',
            phone: userData.phone || '',
            status: userData.status || (userData.isActive ? 'Active' : 'Inactive') || 'Active',
            password: '',
            confirmPassword: '',
            createdDate: userData.createdAt ? new Date(userData.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
          })
        } else {
          toast.error('Failed to load user data')
          navigate('/settings/user-master')
        }
      } catch (error) {
        console.error('Error loading user data:', error)
        toast.error('Failed to load user data')
        navigate('/settings/user-master')
      } finally {
        setInitialLoading(false)
      }
    }

    if (id) {
      loadUserData()
    }
  }, [id, navigate])

  const loadRoles = async () => {
    try {
      // Import roleService to get roles
      const { default: roleService } = await import('../../services/roleService')
      const response = await roleService.getAllRoles()
      if (response.success) {
        setRoles(response.data)
      }
    } catch (error) {
      console.error('Error loading roles:', error)
      // Fallback to hardcoded roles if API fails
      setRoles([
        { _id: '1', roleName: 'Super Admin' },
        { _id: '2', roleName: 'Admin' },
        { _id: '3', roleName: 'Sales Manager' },
        { _id: '4', roleName: 'Sales Admin' },
        { _id: '5', roleName: 'Purchase Manager' },
        { _id: '6', roleName: 'Purchase Admin' }
      ])
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      
      // Check all required fields
      const requiredFields = [
        { field: 'userCode', label: 'User Code' },
        { field: 'name', label: 'Full Name' },
        { field: 'email', label: 'Email Address' },
        { field: 'role', label: 'Role' },
        { field: 'department', label: 'Department' },
        { field: 'designation', label: 'Designation' },
        { field: 'status', label: 'Status' }
      ]

      const missingFields = requiredFields.filter(field => !formData[field.field] || formData[field.field].trim() === '')
      
      if (missingFields.length > 0) {
        toast.error(`Please fill in: ${missingFields.map(f => f.label).join(', ')}`)
        return
      }

      // Only validate passwords if they are provided
      if (formData.password && formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match')
        return
      }

      const userData = userService.transformUserForSubmission(formData)
      const response = await userService.updateUser(id, userData)
      
      if (response.success) {
        toast.success('User updated successfully!', { autoClose: 2000 })
        navigate('/settings/user-master')
      } else {
        toast.error(response.message || 'Failed to update user')
      }
    } catch (error) {
      console.error('Error updating user:', error)
      toast.error('Failed to update user: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    // Reset to original data
    const loadUserData = async () => {
      try {
        const response = await userService.getUserById(id)
        if (response.success) {
          const userData = response.data
          const display = userService.transformUserForDisplay(userData)
          const firstRole = Array.isArray(userData.role) && userData.role[0]
          const roleId = display?.roleId || (firstRole && (typeof firstRole === 'object' ? (firstRole._id || firstRole.id) : firstRole)) || ''
          const departmentValue = display?.departmentName || userData.departmentName || (userData.department?.name) || ''
          setFormData({
            userCode: userData.userCode || '',
            name: userData.name || '',
            email: userData.email || userData.userEmail || '',
            userEmail: userData.userEmail || userData.email || '',
            role: roleId,
            department: departmentValue,
            designation: userData.designation || '',
            phone: userData.phone || '',
            status: userData.status || (userData.isActive ? 'Active' : 'Inactive') || 'Active',
            password: '',
            confirmPassword: '',
            createdDate: userData.createdAt ? new Date(userData.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
          })
        }
      } catch (error) {
        console.error('Error loading user data:', error)
      }
    }
    loadUserData()
  }

  const handleBack = () => {
    navigate('/settings/user-master')
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Settings',
      path: '/settings',
      onClick: () => navigate('/settings')
    },
    {
      label: 'Masters',
      path: '/settings',
      onClick: () => navigate('/settings')
    },
    {
      label: 'User Master',
      path: '/settings/user-master',
      isCurrent: true
    }
  ]

  if (initialLoading) {
    return (
      <div className="sales-dashboard">
        <div className="page-header">
          <div className="page-title">
            <h1>Loading...</h1>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>User Master - Edit</h1>
          <p>Edit user account and permissions</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>User Master</h2>
        </div>
          
        <form className="form-content">
          {/* Row 1: User Code, Full Name, Email Address, Phone Number */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="userCode" className="form-label required">
                User Code
              </label>
              <input
                type="text"
                id="userCode"
                name="userCode"
                value={formData.userCode}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter user code"
              />
            </div>

            <div className="form-group">
              <label htmlFor="name" className="form-label required">
                Full Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter full name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label required">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter email address"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone" className="form-label">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter phone number"
              />
            </div>
          </div>

          {/* Row 2: Role, Department, Designation, Status */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="role" className="form-label required">
                Role
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="">Select Role</option>
                {roles.map((role) => (
                  <option key={role._id} value={role._id}>
                    {role.roleName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="department" className="form-label required">
                Department
              </label>
              <input
                type="text"
                id="department"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter department"
              />
            </div>

            <div className="form-group">
              <label htmlFor="designation" className="form-label required">
                Designation
              </label>
              <input
                type="text"
                id="designation"
                name="designation"
                value={formData.designation}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter designation"
              />
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label required">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Row 3: Password, Confirm Password, Created Date */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter new password (leave blank to keep current)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Confirm new password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="createdDate" className="form-label required">
                Created Date
              </label>
              <div className="date-input-wrapper">
                <input
                  type="date"
                  id="createdDate"
                  name="createdDate"
                  value={formData.createdDate}
                  onChange={handleInputChange}
                  className="form-input date-input"
                />
                <Calendar size={16} className="calendar-icon" />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn-primary"
            >
              <Save size={16} />
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default UserMasterEdit